import { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axiosInstance from '../utils/axios';

// For development - set to true to add artificial delay to API calls
const ADD_ARTIFICIAL_DELAY = false;
const ARTIFICIAL_DELAY_MS = 1000; // 1 second delay for testing

interface LoadingContextType {
  isLoading: boolean;
  pendingRequests: number;
}

const LoadingContext = createContext<LoadingContextType>({
  isLoading: false,
  pendingRequests: 0
});

export const useLoading = () => useContext(LoadingContext);

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider = ({ children }: LoadingProviderProps) => {
  const [pendingRequests, setPendingRequests] = useState(0);
  
  useEffect(() => {
    // Add request interceptor
    const requestInterceptor = axiosInstance.interceptors.request.use(
      (config) => {
        setPendingRequests((prev) => prev + 1);
        return config;
      },
      (error) => {
        setPendingRequests((prev) => Math.max(0, prev - 1));
        return Promise.reject(error);
      }
    );

    // Add response interceptor
    const responseInterceptor = axiosInstance.interceptors.response.use(
      async (response) => {
        // For development only - add artificial delay
        if (ADD_ARTIFICIAL_DELAY) {
          await new Promise(resolve => setTimeout(resolve, ARTIFICIAL_DELAY_MS));
        }
        
        setPendingRequests((prev) => Math.max(0, prev - 1));
        return response;
      },
      (error) => {
        setPendingRequests((prev) => Math.max(0, prev - 1));
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors when component unmounts
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const value = {
    isLoading: pendingRequests > 0,
    pendingRequests
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
}; 